import { alpha, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGridPro, DataGridProProps, GridOverlay } from '@mui/x-data-grid-pro';
import { DEFAULT_PAGE_SIZE } from 'src/models/extensions/withPagination';

/*
 * << 컴포넌트 속성 예제 >>
 * 컴포넌트의 상태별 색상
 */
type TCDataGridProColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

/*
 * << 컴포넌트 속성 예제 >>
 * 컴포넌트의 외곽선의 종류
 */
type TCDataGridProVariant = 'filled' | 'outlined' | 'solid' | 'dotted' | 'dashed' | 'ghost';

/*
 * 컴포넌트의 속성
 */
interface ICDataGridProProps extends DataGridProProps {
  color?: TCDataGridProColor;
  variant?: TCDataGridProVariant;
}

// DataGridPro 컴포넌트 커스텀 스타일
const StyledDataGridPro = styled(DataGridPro, {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<{
  ownerState: {
    color: TCDataGridProColor;
    variant: TCDataGridProVariant;
  };
}>(({ theme, ownerState }) => {
  const { color, variant } = ownerState;

  return {
    ...(color === 'primary' && {
      backgroundColor: theme.palette.primary[theme.palette.mode],
    }),
    ...(color === 'secondary' && {
      backgroundColor: theme.palette.secondary[theme.palette.mode],
    }),
    ...(variant === 'solid' && {
      border: '1px solid darkblue',
    }),
    ...(variant === 'dashed' && {
      border: '3px dashed darkred',
    }),

    color: theme.palette.grey[600],
    fontSize: 14,
    userSelect: 'none',
    // flex: 1,
    // '& .MuiDataGrid-main': {
    //   // row 없을 경우 '데이터가 없습니다' 노출을 위한 minHeight 부여
    //   minHeight: '100px',
    // },
    '& .MuiDataGrid-cell:hover': {
      color: theme.palette.text.primary,
    },

    '& .MuiDataGrid-iconSeparator': {
      visibility: 'hidden',
    },

    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: theme.palette.background.paper,
    },

    '.MuiDataGrid-container--top [role=row]': {
      backgroundColor: alpha(theme.palette.grey[500], 0.2),
    },

    '& .MuiDataGrid-columnHeader': {
      borderRight: `1px solid ${alpha(theme.palette.text.disabled, 0.4)}`,
      '&.MuiDataGrid-columnHeader--last': { borderRight: 'none' },
    },

    '& .MuiDataGrid-cell:not(:last-child)': {
      borderBottom: `1px solid ${alpha(theme.palette.text.disabled, 0.4)} `,
      borderRight: `1px solid ${alpha(theme.palette.text.disabled, 0.4)}`,
    },

    '& .MuiPaginationItem-root': {
      borderRadius: 0,
    },
    '& .logo-cell': {
      padding: 0,
      margin: 0,
      background: `linear-gradient(to bottom, ${theme.palette.grey[800]}, ${theme.palette.grey[600]})`,
    },

    // scrollbar
    // '& .MuiDataGrid-scrollbar': {
    //   opacity: 1,
    // },
    // '& .MuiDataGrid-scrollbarContent': {
    //   opacity: 1,
    // },
    // '& .MuiDataGrid-scrollbar--vertical::-webkit-scrollbar': {
    //   width: 10,
    // },
    // '& .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar': {
    //   height: 10,
    // },
    // '& .MuiDataGrid-scrollbar::-webkit-scrollbar-track': {
    //   background: 'transparent',
    // },
    // '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb': {
    //   backgroundColor: alpha(theme.palette.grey[600], 0.48),
    // },
    // '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb:hover': {
    //   backgroundColor: alpha(theme.palette.grey[600], 0.48),
    // },
  };
});

// 데이터가 없을 때 표시할 컴포넌트
const NoRowsOverlay = () => (
  <GridOverlay>
    <div style={{ padding: '16px', textAlign: 'center', width: '100%' }}>데이터가 없습니다</div>
  </GridOverlay>
);

const CDataGridPro = ({
  color = 'default',
  variant = 'ghost',
  sx,
  rows,
  columns,
  ...props
}: ICDataGridProProps) => {
  const theme = useTheme();

  return (
    <StyledDataGridPro
      ownerState={{ color, variant }}
      sx={sx}
      columns={columns}
      rows={rows}
      paginationMode="server"
      pagination
      columnHeaderHeight={48}
      rowHeight={48}
      initialState={{
        pagination: {
          paginationModel: {
            page: 0,
            pageSize: DEFAULT_PAGE_SIZE,
          },
        },
        columns: {
          columnVisibilityModel: {
            id: false,
          },
        },
      }}
      slots={{
        noRowsOverlay: NoRowsOverlay,
      }}
      {...props}
    />
  );
};

export default CDataGridPro;
